import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import {
  isValidEmail,
  suggestEmailDomain as suggestDomain,
  emailsLookLikeTypoPair,
  pickCloserEmailCandidate,
} from './utils/patientContactValidation';
import {
  runEmailStepRegistration,
  isStillInSignupSteps,
  storePendingAccessBlockedMessage,
} from './utils/emailStepRegistration';
import {
  nextSignupRoute,
  prevSignupRoute,
  getSignupVariant,
} from './utils/signupOrderAb';
import { markSignupFieldTyped } from './utils/signupFieldPrefill';
import { usesGuidedCopy, signupUxProgress } from './utils/signupUxAb';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import SignupGreeting, { SIGNUP_GREETING_CSS, readSignupFullName } from './components/SignupGreeting';
import SignupStepHeading from './components/SignupStepHeading';

const BACKEND_URL = process.env.REACT_APP_API_URL || window.location.origin;

const ACCESS_BLOCKED_DEFAULT =
  'Identificamos, em consulta(s) anterior(es) vinculada(s) a este cadastro, o descumprimento de disposições dos nossos Termos de Uso. Por esse motivo, o acesso a novas consultas na plataforma foi bloqueado.';

function AccessBlockedModal({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-confirm-blocked-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10500,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '1rem',
          padding: '1.5rem 1.5rem 1.25rem',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          textAlign: 'left',
        }}
      >
        <h2
          id="email-confirm-blocked-title"
          style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', fontWeight: 700, color: '#B91C1C' }}
        >
          Acesso bloqueado
        </h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#334155', lineHeight: 1.55 }}>
          {message}
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.7rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: '#1E293B',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          Entendi
        </button>
      </div>
    </div>
  );
}

const DRAFT_KEY = 'patientEmailDraft';

function readDraft() {
  try {
    return String(
      sessionStorage.getItem(DRAFT_KEY) || localStorage.getItem(DRAFT_KEY) || '',
    ).trim().toLowerCase();
  } catch {
    return '';
  }
}

function emailDomainHint(raw) {
  const e = String(raw || '').trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (at < 1) return '';
  const dom = e.slice(at + 1);
  return dom ? `@${dom}` : '';
}

/**
 * Confirmação em duas digitações + desempate quando não batem (3ª tentativa +
 * heurística de proximidade). Alinhado a boas práticas de formulário: aviso para
 * não copiar/colar, validação de domínio conhecido e escolha explícita quando
 * os endereços são "quase iguais".
 */
export default function EmailConfirmForm() {
  const draft = readDraft();
  if (!draft || !isValidEmail(draft)) {
    return <Navigate to="/email" replace />;
  }
  return <EmailConfirmFormInner draft={draft} />;
}

function EmailConfirmFormInner({ draft }) {
  const navigate = useNavigate();
  useViewportInsets();

  const [phase, setPhase] = useState('second'); // second | similar_pick | third
  const [confirmEmail, setConfirmEmail] = useState('');
  const [thirdEmail, setThirdEmail] = useState('');
  /** Segunda tentativa salva quando difere do rascunho (para fase 3 / similar). */
  const [secondAttempt, setSecondAttempt] = useState('');
  const [touched, setTouched] = useState(false);
  const [thirdTouched, setThirdTouched] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [thirdSuggestion, setThirdSuggestion] = useState(null);
  /**
   * E-mails que o paciente já declarou corretos em "Não, está correto". Sem
   * isso, quem tem domínio real parecido com um conhecido (@vivo.com.br,
   * @bb.com.br) recebia a mesma sugestão a cada toque em Continuar, sem saída.
   */
  const [typoAcked, setTypoAcked] = useState([]);
  const [thirdError, setThirdError] = useState('');
  const [blockedMessage, setBlockedMessage] = useState(null);
  const inputRef = useRef(null);
  const thirdRef = useRef(null);

  const finalize = useCallback(
    async (trimmed, meta = {}) => {
      const t = String(trimmed || '').trim().toLowerCase();
      if (!isValidEmail(t)) return;
      try {
        sessionStorage.setItem('patientEmail', t);
        localStorage.setItem('patientEmail', t);
        sessionStorage.removeItem(DRAFT_KEY);
        localStorage.removeItem(DRAFT_KEY);
      } catch {}
      markSignupFieldTyped('patientEmail');
      logUxEvent('email_confirmed_double_entry', {
        length: t.length,
        ...meta,
      });
      const patientId = sessionStorage.getItem('patientId') || localStorage.getItem('patientId') || '';
      const fullName = sessionStorage.getItem('fullName') || localStorage.getItem('fullName') || '';

      const variant = getSignupVariant();
      const dest = nextSignupRoute('emailConfirm', variant) || '/cpf';
      const startedAt = Date.now();
      navigate(dest);

      runEmailStepRegistration({
        baseUrl: BACKEND_URL,
        email: t,
        patientId,
        fullName,
        source: 'email_confirm',
        onPaidRecovered: (route) => {
          const inSignup = isStillInSignupSteps();
          logUxEvent('email_paid_dup_redirect', { after_ms: Date.now() - startedAt, still_in_signup: inSignup });
          if (inSignup) navigate(route, { replace: true });
        },
        onBlocked: (message, code) => {
          logUxEvent('email_confirm_access_blocked', { code, after_ms: Date.now() - startedAt });
          storePendingAccessBlockedMessage(message || ACCESS_BLOCKED_DEFAULT);
          if (isStillInSignupSteps()) navigate('/email', { replace: true });
        },
      });
    },
    [navigate],
  );

  const handleSecondRaw = () => {
    setTouched(true);
    setSuggestion(null);
    const possibleFix = typoAcked.includes(String(confirmEmail || '').trim().toLowerCase())
      ? null
      : suggestDomain(confirmEmail);
    if (possibleFix) {
      setSuggestion(possibleFix);
      logUxEvent('email_confirm_typo_blocked', { stage: 'second' });
      return false;
    }
    const s = String(confirmEmail || '').trim().toLowerCase();
    if (!isValidEmail(s)) {
      logUxEvent('email_confirm_validation_error', { stage: 'second' });
      return false;
    }
    if (s === draft) {
      finalize(s, { path: 'exact_match_second' });
      return true;
    }
    setSecondAttempt(s);
    if (emailsLookLikeTypoPair(draft, s)) {
      setPhase('similar_pick');
      logUxEvent('email_confirm_similar_pair', {});
      return true;
    }
    setPhase('third');
    logUxEvent('email_confirm_mismatch_third_step', {});
    return true;
  };

  const [secondSubmitting, handleSecondSubmit] = useFormSubmitGuard(handleSecondRaw);

  const handleThirdRaw = () => {
    setThirdTouched(true);
    setThirdError('');
    setThirdSuggestion(null);
    const possibleFix = typoAcked.includes(String(thirdEmail || '').trim().toLowerCase())
      ? null
      : suggestDomain(thirdEmail);
    if (possibleFix) {
      setThirdSuggestion(possibleFix);
      return false;
    }
    const t = String(thirdEmail || '').trim().toLowerCase();
    if (!isValidEmail(t)) {
      setThirdError('Insira um e-mail válido.');
      return false;
    }
    if (t === draft) {
      finalize(draft, { path: 'third_matches_first' });
      return true;
    }
    if (t === secondAttempt) {
      finalize(secondAttempt, { path: 'third_matches_second' });
      return true;
    }
    const pick = pickCloserEmailCandidate(t, draft, secondAttempt);
    if (pick === 'a') {
      finalize(draft, { path: 'third_closer_to_first' });
      return true;
    }
    if (pick === 'b') {
      finalize(secondAttempt, { path: 'third_closer_to_second' });
      return true;
    }
    setThirdError(
      'Esse e-mail não bate com as tentativas anteriores. Confira letra por letra ou volte para corrigir o primeiro passo.',
    );
    logUxEvent('email_confirm_third_unresolved', {});
    return false;
  };

  const [thirdSubmitting, handleThirdSubmit] = useFormSubmitGuard(handleThirdRaw);
  const guided = usesGuidedCopy();

  const acceptSecondSuggestion = () => {
    if (!suggestion) return;
    setConfirmEmail(suggestion);
    setSuggestion(null);
    logUxEvent('email_confirm_typo_fixed', { stage: 'second' });
  };

  const rejectSecondSuggestion = () => {
    if (!suggestion) return;
    setSuggestion(null);
    setTypoAcked((prev) => [...prev, String(confirmEmail || '').trim().toLowerCase()]);
    logUxEvent('email_confirm_typo_rejected', { stage: 'second' });
  };

  const acceptThirdSuggestion = () => {
    if (!thirdSuggestion) return;
    setThirdEmail(thirdSuggestion);
    setThirdSuggestion(null);
  };

  const rejectThirdSuggestion = () => {
    setThirdSuggestion(null);
    setTypoAcked((prev) => [...prev, String(thirdEmail || '').trim().toLowerCase()]);
  };

  const secondErrorMsg = () => {
    if (suggestion) return null;
    if (!touched) return ' ';
    const trimmed = String(confirmEmail || '').trim();
    if (!trimmed) return 'Digite o e-mail novamente.';
    if (!isValidEmail(trimmed)) return 'Insira um e-mail válido.';
    // Domínio parecido com conhecido: a sugestão acionável aparece no submit.
    return ' ';
  };

  const handleBack = () => {
    if (phase === 'third') {
      setPhase('second');
      setThirdEmail('');
      setThirdError('');
      setThirdTouched(false);
      setThirdSuggestion(null);
      return;
    }
    if (phase === 'similar_pick') {
      setPhase('second');
      setSecondAttempt('');
      return;
    }
    const variant = getSignupVariant();
    const prev = prevSignupRoute('emailConfirm', variant);
    navigate(prev || '/email');
  };

  const domainHint = emailDomainHint(draft);

  const isSecondValid = isValidEmail(confirmEmail) && !suggestion;
  const isThirdValid = isValidEmail(thirdEmail) && !thirdSuggestion;

  return (
    <>
      <style>{`
        ${SIGNUP_GREETING_CSS}
        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --border-subtle: #E5E7EB;
          --error: #D32F2F;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
        }
        body { margin: 0; background: var(--bg-page); font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; color: var(--text-main); }
        .ec-shell { min-height: 100vh; min-height: 100dvh; display: flex; flex-direction: column; background: var(--bg-page); }
        .ec-inner {
          flex: 1; max-width: 480px; width: 100%; margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          display: flex; flex-direction: column; align-items: center; gap: 1.25rem; box-sizing: border-box;
        }
        .ec-head { text-align: center; max-width: 22rem; margin-top: 1rem; margin-bottom: 1rem; }
        .ec-h1 { font-size: 1.25rem; font-weight: 600; margin: 0 0 .75rem; line-height: 1.35; }
        .ec-sub { font-size: .9rem; color: var(--text-dim); line-height: 1.45; margin: 0; }
        .ec-card {
          width: 100%; max-width: 400px; background: var(--surface); border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm); padding: 1.25rem; border: 1px solid rgba(0,0,0,0.06); box-sizing: border-box;
        }
        .ec-label { font-size: .8rem; font-weight: 500; margin-bottom: .35rem; display: block; }
        .ec-hint { font-size: .7rem; color: var(--text-dim); margin-bottom: .5rem; }
        .ec-field {
          width: 100%; border: 1px solid var(--border-subtle); border-radius: var(--radius-lg);
          padding: 0; overflow: hidden; box-sizing: border-box;
        }
        .ec-field:focus-within { border: 2px solid var(--primary); box-shadow: 0 0 0 3px rgba(211,47,47,0.10); }
        .ec-input {
          width: 100%; border: none; outline: none; font-size: 1.05rem; padding: 1rem 1.1rem;
          box-sizing: border-box; font-family: inherit;
        }
        .ec-err { min-height: 1.25rem; font-size: .8rem; color: var(--error); margin-top: .5rem; font-weight: 500; }
        .ec-btn-row { display: flex; justify-content: center; margin-top: 1rem; }
        .ec-btn {
          background: var(--primary); color: #fff; border: none; border-radius: var(--radius-full);
          font-size: 1.05rem; font-weight: 600; padding: 1rem 1.5rem; min-width: 200px; min-height: 48px;
          cursor: pointer; font-family: inherit;
        }
        .ec-btn:disabled { opacity: .55; cursor: not-allowed; }
        .ec-typo {
          background: #FFF8E1; border: 1px solid #FFD54F; border-radius: var(--radius-lg);
          padding: .75rem .9rem; margin-top: .6rem; font-size: .9rem; color: #5D4037;
        }
        .ec-typo-row { display: flex; gap: .5rem; margin-top: .5rem; flex-wrap: wrap; }
        .ec-typo-btn { flex: 1; min-width: 120px; padding: .5rem .75rem; border-radius: var(--radius-full); font-weight: 600; cursor: pointer; font-family: inherit; border: none; }
        .ec-typo-yes { background: var(--primary); color: #fff; }
        .ec-typo-no { background: transparent; border: 1px solid rgba(93,64,55,0.25); color: #5D4037; }
        .ec-pick-btns { display: flex; flex-direction: column; gap: .6rem; margin-top: .75rem; }
        .ec-pick {
          text-align: left; padding: .85rem 1rem; border-radius: var(--radius-lg); border: 1px solid var(--border-subtle);
          background: #fafafa; cursor: pointer; font-size: .9rem; word-break: break-all; font-family: inherit;
        }
        .ec-pick:hover { border-color: var(--primary); }
        .ec-muted { font-size: .78rem; color: var(--text-dim); margin-top: .75rem; line-height: 1.4; }
        .ec-linkish {
          margin-top: .5rem; background: none; border: none; color: var(--primary); font-weight: 600;
          cursor: pointer; font-size: .85rem; font-family: inherit; text-decoration: underline;
        }
      `}</style>

      <div className="ec-shell">
        <div className="ec-inner">
          <FormBackButton onClick={handleBack} />

          <ProgressBar {...signupUxProgress('/email/confirm', getSignupVariant())} />

          <div className="ec-head">
            {guided ? <SignupGreeting fullName={readSignupFullName()} /> : null}
            <h1 className="ec-h1 deco-headline">
              {phase === 'third' ? 'Última verificação do e-mail' : 'Confirme seu e-mail'}
            </h1>
            <p className="ec-sub">
              {phase === 'similar_pick'
                ? 'Os dois endereços são parecidos, mas não idênticos. Escolha o correto:'
                : phase === 'third'
                  ? 'As duas tentativas não coincidem. Digite seu e-mail mais uma vez, com atenção (sem copiar e colar).'
                  : `Digite novamente o mesmo e-mail${domainHint ? ` (${domainHint})` : ''}. Evite copiar e colar — assim reduzimos erros de digitação nas receitas.`}
            </p>
            <DecoDivider variant="chevron" />
          </div>

          {phase === 'similar_pick' ? (
            <div className="ec-card">
              {guided ? <SignupStepHeading title={signupStepTitle('/email/confirm')} /> : null}
              <div className="ec-pick-btns">
                <button type="button" className="ec-pick" onClick={() => finalize(draft, { path: 'user_pick_first' })}>
                  Usar: <strong>{draft}</strong>
                </button>
                <button
                  type="button"
                  className="ec-pick"
                  onClick={() => finalize(secondAttempt, { path: 'user_pick_second' })}
                >
                  Usar: <strong>{secondAttempt}</strong>
                </button>
              </div>
              <button type="button" className="ec-linkish" onClick={() => setPhase('second')}>
                Corrigir e digitar novamente
              </button>
            </div>
          ) : phase === 'third' ? (
            <form
              className="ec-card"
              onSubmit={(e) => {
                e.preventDefault();
                handleThirdSubmit();
              }}
              noValidate
            >
              {guided ? <SignupStepHeading title={signupStepTitle('/email/confirm')} /> : null}
              <label className="ec-label" htmlFor="email-third">
                E-mail (terceira digitação)
              </label>
              <div className="ec-hint">Se estiver igual a uma das tentativas anteriores, seguimos automaticamente.</div>
              <div className="ec-field">
                <input
                  id="email-third"
                  ref={thirdRef}
                  className="ec-input"
                  type="email"
                  name="email_confirm_step3"
                  autoComplete="off"
                  inputMode="email"
                  spellCheck={false}
                  enterKeyHint="done"
                  value={thirdEmail}
                  onChange={(e) => {
                    setThirdEmail(e.target.value);
                    setThirdSuggestion(null);
                    setThirdError('');
                    if (!thirdTouched) setThirdTouched(true);
                  }}
                />
              </div>
              {thirdSuggestion ? (
                <div className="ec-typo" role="alert">
                  Você quis dizer <strong>{thirdSuggestion}</strong>?
                  <div className="ec-typo-row">
                    <button type="button" className="ec-typo-btn ec-typo-yes" onClick={acceptThirdSuggestion}>
                      Sim
                    </button>
                    <button type="button" className="ec-typo-btn ec-typo-no" onClick={rejectThirdSuggestion}>
                      Não
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {thirdError ? <div className="ec-err">{thirdError}</div> : null}
                  {!thirdError && thirdTouched && !isThirdValid ? (
                    <div className="ec-err">Insira um e-mail válido.</div>
                  ) : null}
                </>
              )}
              <div className="ec-btn-row">
                <button type="submit" className="ec-btn" disabled={!isThirdValid || thirdSubmitting}>
                  {signupCta('emailConfirmLast', { guided, submitting: thirdSubmitting })}
                </button>
              </div>
              <p className="ec-muted">
                Primeira tentativa (referência): <strong>{draft}</strong>
              </p>
            </form>
          ) : (
            <form
              className="ec-card"
              onSubmit={(e) => {
                e.preventDefault();
                handleSecondSubmit();
              }}
              noValidate
            >
              {guided ? <SignupStepHeading title={signupStepTitle('/email/confirm')} /> : null}
              <label className="ec-label" htmlFor="email-confirm-2">
                Digite o e-mail novamente
              </label>
              <div className="ec-field">
                <input
                  id="email-confirm-2"
                  ref={inputRef}
                  className="ec-input"
                  type="email"
                  name="email_confirm_step2"
                  autoComplete="off"
                  inputMode="email"
                  spellCheck={false}
                  enterKeyHint="done"
                  value={confirmEmail}
                  onChange={(e) => {
                    setConfirmEmail(e.target.value);
                    setSuggestion(null);
                    if (!touched) setTouched(true);
                  }}
                />
              </div>
              {suggestion ? (
                <div className="ec-typo" role="alert">
                  Você quis dizer <strong>{suggestion}</strong>?
                  <div className="ec-typo-row">
                    <button type="button" className="ec-typo-btn ec-typo-yes" onClick={acceptSecondSuggestion}>
                      Sim
                    </button>
                    <button type="button" className="ec-typo-btn ec-typo-no" onClick={rejectSecondSuggestion}>
                      Não
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ec-err" role={touched && !isSecondValid ? 'alert' : undefined}>
                  {secondErrorMsg()}
                </div>
              )}
              <div className="ec-btn-row">
                <button type="submit" className="ec-btn" disabled={!isSecondValid || secondSubmitting}>
                  {signupCta('emailConfirm', { guided, submitting: secondSubmitting })}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <AccessBlockedModal
        message={blockedMessage}
        onClose={() => {
          setBlockedMessage(null);
          navigate('/');
        }}
      />
    </>
  );
}
